/**
 * Prompt Editor Component
 * Feature: 015-custom-prompts
 * Date: 2026-01-04
 *
 * Textarea-based editor for custom prompt templates with:
 * - Real-time validation
 * - Character counter
 * - Error and warning display
 * - Save button (disabled when invalid)
 */

import { memo, useCallback, ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { usePromptValidation } from '@/hooks/usePromptValidation';
import { PROMPT_VALIDATION } from '@/@types/prompts';
import { AlertCircle, AlertTriangle, Save } from 'lucide-react';

interface IPromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  isSaving?: boolean;
  label?: string;
}

const PromptEditor = ({
  value,
  onChange,
  onSave,
  isSaving = false,
  label = 'Custom Prompt Template',
}: IPromptEditorProps) => {
  // Validation with 200ms debounce
  const { isValid, errors, warnings, length } = usePromptValidation(value);

  // Character count colors
  const charCountColor = (() => {
    if (length === 0) return 'text-gray-400';
    if (length < PROMPT_VALIDATION.MIN_LENGTH) return 'text-red-600';
    if (length > PROMPT_VALIDATION.MAX_LENGTH * 0.9) return 'text-orange-600';
    return 'text-gray-600';
  })();

  // Handle textarea change
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  // Handle save
  const handleSave = useCallback(() => {
    if (isValid && !isSaving) {
      onSave();
    }
  }, [isValid, isSaving, onSave]);

  return (
    <div className="space-y-4">
      {/* Label and Character Counter */}
      <div className="flex items-center justify-between">
        <Label htmlFor="prompt-editor" className="text-base font-semibold">
          {label}
        </Label>
        <span className={`text-sm font-medium ${charCountColor}`}>
          {length} / {PROMPT_VALIDATION.MAX_LENGTH}
        </span>
      </div>

      {/* Textarea */}
      <Textarea
        id="prompt-editor"
        value={value}
        onChange={handleChange}
        placeholder="Enter your custom prompt template here..."
        className="min-h-[400px] font-mono text-sm"
        disabled={isSaving}
      />

      {/* Validation Errors */}
      {errors.length > 0 && (
        <div className="space-y-2">
          {errors.map(error => (
            <div
              key={error}
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ))}
        </div>
      )}

      {/* Validation Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-2">
          {warnings.map(warning => (
            <div
              key={warning}
              className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={!isValid || isSaving} className="min-w-[120px]">
          <Save className="mr-2 h-4 w-4" />
          {isSaving ? 'Saving...' : 'Save Prompt'}
        </Button>
      </div>
    </div>
  );
};

export const PromptEditorMemo = memo(PromptEditor);
